import { createSelector } from 'reselect';
import visTypeMap from './visTypes';
import { timeGrainOptions } from './utils/playMasterData';
import moment from 'moment';
import { isNonEmpty } from '../../../utils/validators';
import Chartify from  '@shipsy/chartify';

const normalOptionToSelectOption = elem => ({
  key: elem.id,
  label: elem.name,
});

const timeGrainOptionsForSelect = timeGrainOptions.map(normalOptionToSelectOption);
// const chartifyInstance =  new Chartify.CommonData();

const getMetricList = state => state.masterData.metrics;
const getMetricOptonsFromState = createSelector(
  [getMetricList],
  metricList => ({
    options: metricList.map(normalOptionToSelectOption),
  }),
);

const getDimensionList = (state, withTime) => state.masterData.dimensions;
const getWithTime = (state, withTime) => withTime || false;
const getDimensionOptionsFromState = createSelector(
  [getDimensionList, getWithTime],
  (dimensionList, withTime) => {
    let optionToSend = dimensionList.map(normalOptionToSelectOption);
    if (withTime) {
      optionToSend = optionToSend.concat({
        key: 'timestamp',
        label: 'Time',
      });
    }
    return optionToSend;
  },
);

const getExceptionRules = (state: any) => state.masterData.exceptionRules;
const getExceptionRulesOptions = createSelector(
  [getExceptionRules],
  (exceptionRules: any) => {
    const exceptionRulesOptions = (exceptionRules && exceptionRules.length) ?
      exceptionRules.map((exceptionRule:any) => ({
        key: exceptionRule.id,
        label: exceptionRule.name,
      })) : [];
    return { options: exceptionRulesOptions };
  },
);

const getDashboardAndSetDefault = (state: any, control) => state.masterData.dashboards;
const getSelectDashboardLayout = createSelector(
  [getDashboardAndSetDefault],
  (dashboards: any) => {
    const selectDashboardLayout = (dashboards && dashboards.length) ?
      dashboards
        .map(dashboard => ({ key: dashboard.id, label : dashboard.name })) : [] ;
    return { options: selectDashboardLayout };
  },
);

const dimensionMapStateToProps = (state, control) => {
  let value = control.value;
  const withTime = control.withTime || false;
  if (!withTime && value === 'time') {
    value = undefined;
  }
  return {
    value,
    options: getDimensionOptionsFromState(state, withTime),
  };
};

// const getDimensionOptionsFromStateWithTime = createSelector(
//   [getDimensionList],
//   dimensionList => ({
//     options: dimensionList
//       .map(normalOptionToSelectOption)
//       .concat({
//         key: 'time',
//         label: 'Time',
//       }),
//   }),
// );

const filterMapStateToProps = (state) => {
  const { masterData } = state;
  return {
    dimensions: masterData.dimensions,
    dimensionData: masterData.dimensionData,
  };
};

const visualisationOptions = Object.keys(visTypeMap).map(visType => ({
  key: visType,
  label: visTypeMap[visType].label,
}));

const controlTypes = {
  metric_multiple: {
    type: 'MultiSelectControl',
    label: 'Metric',
    mapStateToProps: getMetricOptonsFromState,
    validators: [isNonEmpty],
  },
  metric: {
    type: 'SelectControl',
    label: 'Metric',
    mapStateToProps: getMetricOptonsFromState,
    validators: [isNonEmpty],
  },
  metric_2: {
    type: 'SelectControl',
    label: 'Secondary Metric',
    mapStateToProps: getMetricOptonsFromState,
    validators: [isNonEmpty],
  },
  secondary_dimension: {
    type: 'SelectControl',
    label: 'Secondary Dimension',
    withTime: true,
    mapStateToProps: dimensionMapStateToProps,
  },
  primary_dimension: {
    type: 'SelectControl',
    label: 'Primary Dimension',
    withTime: true,
    mapStateToProps: dimensionMapStateToProps,
    validators: [isNonEmpty],
  },
  x_axis_label: {
    type: 'TextControl',
    label: 'X Axis Label',
    renderTrigger: true,
    default: '',
  },
  y_axis_label: {
    type: 'TextControl',
    label: 'Y Axis Label',
    renderTrigger: true,
    default: '',
  },
  y_axis_2_label: {
    type: 'TextControl',
    label: 'Secondary Y Axis Label',
    renderTrigger: true,
    default: '',
  },
  linkTo: {
    type: 'SelectControl',
    label: 'Link To',
    mapStateToProps: getSelectDashboardLayout,
    default: '',

  },
  visualisation_type: {
    type: 'SelectControl',
    label: 'Visualisation Type',
    options: visualisationOptions,
    default: controlData => controlData.options[0].key,
    alwaysHasValue: true,
  },

  // secondary_dimension_with_time: {
  //   type: 'SelectControl',
  //   label: 'Secondary Dimension',
  //   mapStateToProps: getDimensionOptionsFromStateWithTime,
  // },
  // primary_dimension_with_time: {
  //   type: 'SelectControl',
  //   label: 'Primary Dimension',
  //   mapStateToProps: getDimensionOptionsFromStateWithTime,
  // },
  primary_dimension_time_grain: {
    type: 'SelectControl',
    label: 'Time Grain',
    options: timeGrainOptionsForSelect,
    default: controlData => controlData.options[0].key,
    alwaysHasValue: true,
    validators: [isNonEmpty],
  },
  secondary_dimension_time_grain: {
    type: 'SelectControl',
    label: 'Time Grain',
    options: timeGrainOptionsForSelect,
    default: controlData => controlData.options[0].key,
    alwaysHasValue: true,
    validators: [isNonEmpty],
  },
  duration: {
    type: 'DurationControl',
    label: 'Duration',
    default: {
      start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      start_type: 'fixed',
      start_relative_number: 1,
      start_relative_grain: 'day',

      end_date: moment().format('YYYY-MM-DD'),
      end_type: 'fixed',
      end_relative_number: 1,
      end_relative_grain: 'day',
    },
  },
  filter: {
    type: 'FilterControl',
    label: 'Filters',
    default: [],
    mapStateToProps: filterMapStateToProps,
  },
  doughnut: {
    type: 'CheckboxControl',
    label: '',
    default: true,
    renderTrigger: true,
    text: 'Enable Doughnut',
  },
  bar_stacked: {
    type: 'CheckboxControl',
    label: '',
    default: true,
    renderTrigger: true,
    text: 'Enable Stacking',
  },
  exception_rule: {
    type: 'SelectControl',
    label: 'Exception Rule',
    mapStateToProps: getExceptionRulesOptions,
    validators: [isNonEmpty],
  },
  exception_card_type: {
    type: 'RadioControl',
    label: 'Card Display',
    default: 'table',
    options: [{ key: 'table', label: 'Table' }, { key: 'count', label: 'Count' }],
  },
  bar_type: {
    type: 'RadioControl',
    label: 'Placement',
    default: 'vertical',
    options: [{ key: 'horizontal', label: 'Horizontal' }, { key: 'vertical', label: 'Vertical' }],
  },
  show_trend_line: {
    type: 'RadioControl',
    label: 'Show Trendline',
    default: true,
    options: [{ key: true, label: 'Yes' },
      { key: false, label: 'No' }],
  },
  aggregation: {
    type: 'SelectControl',
    label: 'Aggregation',
    validators: [isNonEmpty],
    options: [{
      key: 'sum',
      label: 'Sum',
    }, {
      key: 'avg',
      label: 'Average',
    }, {
      key: 'max',
      label: 'Maximum',
    }, {
      key: 'min',
      label: 'Minimum',
    }],
    default: controlData => controlData.options[0].key,
    alwaysHasValue: true,

  },
  colorPicker: {
    type: 'ColorControl',
    label: 'Color Picker',
    options: [{
      key: 'sum',
      label: 'Sum',
    }, {
      key: 'avg',
      label: 'Average',
    }, {
      key: 'max',
      label: 'Maximum',
    }, {
      key: 'min',
      label: 'Minimum',
    }],
    default: controlData => controlData.options[0].key,
    alwaysHasValue: true,
  },
};

export default controlTypes;

export const mapControlDataToFormDataByComponent = {
  // MultiSelectControl: (controlData) => {
  //   if (controlData.value) {
  //     return controlData.value.map(elem => elem.key);
  //   }
  //   return [];
  // },

  // SelectControl: (controlData) => {
  //   if (controlData.value) {
  //     return controlData.value.key;
  //   }
  //   return null;
  // },
};
